import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Search, ChevronDown, ChevronRight, Ticket, MapPin, Calendar, X, Maximize2 } from 'lucide-react';
import { Toaster } from 'sonner';
import { findStadium, applyCustomPlans } from '../../data/stadiums';
import { getOfficialMatch, getMatchPhase } from '../../data/wc2026Calendar';

// ====== I18N : Libellés FR / EN ======
const LABELS = {
  fr: {
    listPublic: 'Liste publique en temps réel des billets disponibles.',
    inStock: 'en stock',
    ticket: 'billet', tickets: 'billets',
    match: 'match', matches: 'matchs',
    expandAll: 'Tout déplier', collapseAll: 'Tout replier',
    matchesLbl: 'Matchs', catsLbl: 'Catégories',
    search: 'Rechercher : match, catégorie, block...',
    plan: 'Plan', stadiumPlan: 'Plan du stade',
    onDemand: 'Sur demande',
    noTickets: 'Aucun billet disponible pour le moment',
    comeBack: 'Reviens bientôt 🎫',
    poweredBy: 'Powered by YAS · Mise à jour en temps réel',
    block: 'Block', row: 'Rang', seat: 'Place', price: 'Prix',
    loading: 'Chargement des billets...',
    group: 'Groupe',
    today: "Aujourd'hui", tomorrow: 'Demain', past: 'Passé',
  },
  en: {
    listPublic: 'Live public listing of available tickets.',
    inStock: 'in stock',
    ticket: 'ticket', tickets: 'tickets',
    match: 'match', matches: 'matches',
    expandAll: 'Expand all', collapseAll: 'Collapse all',
    matchesLbl: 'Matches', catsLbl: 'Categories',
    search: 'Search: match, category, block...',
    plan: 'Map', stadiumPlan: 'Stadium map',
    onDemand: 'On request',
    noTickets: 'No tickets available at the moment',
    comeBack: 'Come back soon 🎫',
    poweredBy: 'Powered by YAS · Real-time updates',
    block: 'Block', row: 'Row', seat: 'Seat', price: 'Price',
    loading: 'Loading tickets...',
    group: 'Group',
    today: 'Today', tomorrow: 'Tomorrow', past: 'Past',
  },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sur le sous-domaine public custom (ex: world-cup.kr-buyer.com), il faut
// utiliser une URL d'API relative pour éviter les blocages CORS cross-domain.
const isCustomPublicDomain = typeof window !== 'undefined' && (() => {
  try {
    const host = window.location.hostname.toLowerCase();
    return !host.endsWith('emergentagent.com') && !host.endsWith('emergent.host') && !host.includes('localhost');
  } catch { return false; }
})();
const API_BASE = isCustomPublicDomain ? '' : BACKEND_URL;

const projectDisplayName = (slug) => {
  const map = {
    'world-cup-2026': 'FIFA World Cup 2026',
    'roland-garros': 'Roland Garros',
    'psg': 'Paris Saint-Germain',
    'ffr': 'FFR',
  };
  return map[slug.toLowerCase()] || slug;
};

const projectHeroImage = (slug) => {
  // Logos officiels hostés (uploaded par l'utilisateur)
  const map = {
    'world-cup-2026': 'https://customer-assets.emergentagent.com/job_289d1ff2-678c-4880-b158-8ad7c1edcdb9/artifacts/hfg14asi_te%CC%81le%CC%81chargement.jpeg',
  };
  return map[slug.toLowerCase()] || null;
};

// Visuel inline (SVG) par défaut : ballon de foot stylé pour les projets sport
const ProjectFallbackVisual = ({ slug }) => {
  const isFootball = ['world-cup-2026', 'psg', 'ffr'].some(s => slug.toLowerCase().includes(s.split('-')[0]));
  const isTennis = slug.toLowerCase().includes('roland') || slug.toLowerCase().includes('rg');
  if (isFootball) {
    return (
      <div className="relative h-36 w-36 sm:h-44 sm:w-44 shrink-0 hidden sm:flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400/40 via-amber-500/10 to-transparent rounded-full blur-2xl" />
        <svg viewBox="0 0 100 100" className="relative h-full w-full drop-shadow-[0_8px_30px_rgba(251,191,36,0.35)]">
          <defs>
            <radialGradient id="ballGrad" cx="35%" cy="30%" r="75%">
              <stop offset="0%" stopColor="#fff" />
              <stop offset="60%" stopColor="#d4d4d8" />
              <stop offset="100%" stopColor="#52525b" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="46" fill="url(#ballGrad)" stroke="#18181b" strokeWidth="1.5" />
          <polygon points="50,28 62,37 57,52 43,52 38,37" fill="#0a0a0a" />
          <polygon points="38,37 25,42 22,57 32,67 43,52" fill="#0a0a0a" opacity="0.85" />
          <polygon points="62,37 75,42 78,57 68,67 57,52" fill="#0a0a0a" opacity="0.85" />
          <polygon points="43,52 32,67 40,80 60,80 68,67 57,52" fill="#0a0a0a" opacity="0.7" />
        </svg>
      </div>
    );
  }
  if (isTennis) {
    return (
      <div className="relative h-36 w-36 sm:h-44 sm:w-44 shrink-0 hidden sm:flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-400/40 via-orange-500/10 to-transparent rounded-full blur-2xl" />
        <svg viewBox="0 0 100 100" className="relative h-full w-full drop-shadow-[0_8px_30px_rgba(251,146,60,0.35)]">
          <defs>
            <radialGradient id="tennisGrad" cx="35%" cy="30%" r="75%">
              <stop offset="0%" stopColor="#fef08a" />
              <stop offset="60%" stopColor="#eab308" />
              <stop offset="100%" stopColor="#854d0e" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="46" fill="url(#tennisGrad)" stroke="#18181b" strokeWidth="1.5" />
          <path d="M 12 50 Q 30 30, 50 50 T 88 50" stroke="#fff" strokeWidth="2.5" fill="none" />
          <path d="M 12 50 Q 30 70, 50 50 T 88 50" stroke="#fff" strokeWidth="2.5" fill="none" />
        </svg>
      </div>
    );
  }
  return null;
};

// Mapping noms d'équipes/pays → code ISO 2 lettres pour flagcdn.com
const COUNTRY_TO_ISO = {
  // Confédération CONCACAF (Hôtes WC 2026)
  'mexico': 'mx', 'mexique': 'mx', 'mex': 'mx',
  'usa': 'us', 'united states': 'us', 'us': 'us', 'etats-unis': 'us', 'etats unis': 'us',
  'canada': 'ca', 'can': 'ca',
  // Confédération CAF (Afrique)
  's.africa': 'za', 'south africa': 'za', 'afrique du sud': 'za', 'rsa': 'za',
  'maroc': 'ma', 'morocco': 'ma', 'mar': 'ma',
  'algerie': 'dz', 'algeria': 'dz', 'alg': 'dz',
  'tunisie': 'tn', 'tunisia': 'tn',
  'egypte': 'eg', 'egypt': 'eg',
  'senegal': 'sn',
  'cameroun': 'cm', 'cameroon': 'cm',
  'nigeria': 'ng', 'nig': 'ng',
  'ghana': 'gh',
  'cote ivoire': 'ci', "cote d'ivoire": 'ci', 'ivory coast': 'ci', "côte d'ivoire": 'ci', 'cote-divoire': 'ci', 'civ': 'ci',
  'cabo verde': 'cv', 'cape verde': 'cv', 'cap-vert': 'cv', 'cap vert': 'cv', 'cab': 'cv', 'cpv': 'cv',
  'mali': 'ml',
  'burkina faso': 'bf', 'burkina': 'bf',
  'rd congo': 'cd', 'congo': 'cd', 'drc': 'cd', 'dr congo': 'cd', 'r.d. congo': 'cd', 'd.r. congo': 'cd', 'cod': 'cd', 'congo dr': 'cd',
  'madagascar': 'mg',
  'kenya': 'ke',
  'ouganda': 'ug', 'uganda': 'ug',
  'guinee': 'gn', 'guinea': 'gn',
  'gabon': 'ga',
  // Confédération CONMEBOL (Amérique du Sud)
  'brazil': 'br', 'bresil': 'br', 'brasil': 'br', 'bra': 'br',
  'argentine': 'ar', 'argentina': 'ar', 'arg': 'ar',
  'uruguay': 'uy', 'uru': 'uy',
  'colombie': 'co', 'colombia': 'co', 'col': 'co',
  'chili': 'cl', 'chile': 'cl',
  'perou': 'pe', 'peru': 'pe',
  'equateur': 'ec', 'ecuador': 'ec',
  'paraguay': 'py', 'par': 'py',
  'venezuela': 've', 'ven': 've',
  'bolivie': 'bo', 'bolivia': 'bo',
  // Confédération UEFA (Europe)
  'france': 'fr', 'fra': 'fr',
  'espagne': 'es', 'spain': 'es', 'esp': 'es',
  'allemagne': 'de', 'germany': 'de', 'ger': 'de', 'deutschland': 'de',
  'italie': 'it', 'italy': 'it', 'ita': 'it',
  'angleterre': 'gb-eng', 'england': 'gb-eng', 'eng': 'gb-eng',
  'portugal': 'pt', 'por': 'pt',
  'pays-bas': 'nl', 'netherlands': 'nl', 'holland': 'nl', 'ned': 'nl',
  'belgique': 'be', 'belgium': 'be', 'bel': 'be',
  'croatie': 'hr', 'croatia': 'hr', 'cro': 'hr',
  'suisse': 'ch', 'switzerland': 'ch', 'sui': 'ch',
  'pologne': 'pl', 'poland': 'pl', 'pol': 'pl',
  'turquie': 'tr', 'turkey': 'tr', 'tur': 'tr',
  'danemark': 'dk', 'denmark': 'dk', 'den': 'dk',
  'suede': 'se', 'sweden': 'se', 'swe': 'se',
  'norvege': 'no', 'norway': 'no', 'nor': 'no',
  'serbie': 'rs', 'serbia': 'rs', 'srb': 'rs',
  'autriche': 'at', 'austria': 'at', 'aut': 'at',
  'ukraine': 'ua', 'ukr': 'ua',
  'ecosse': 'gb-sct', 'scotland': 'gb-sct', 'sco': 'gb-sct',
  'pays de galles': 'gb-wls', 'wales': 'gb-wls', 'wal': 'gb-wls',
  'irlande': 'ie', 'ireland': 'ie', 'irl': 'ie',
  'republique tcheque': 'cz', 'czechia': 'cz', 'cze': 'cz', 'czech republic': 'cz',
  'slovaquie': 'sk', 'slovakia': 'sk', 'svk': 'sk',
  'hongrie': 'hu', 'hungary': 'hu', 'hun': 'hu',
  'roumanie': 'ro', 'romania': 'ro', 'rou': 'ro',
  'grece': 'gr', 'greece': 'gr', 'gre': 'gr',
  'islande': 'is', 'iceland': 'is', 'isl': 'is',
  'finlande': 'fi', 'finland': 'fi', 'fin': 'fi',
  'bosnie': 'ba', 'bosnia': 'ba', 'bosnia and herzegovina': 'ba', 'bosnia-herzegovina': 'ba', 'bos': 'ba', 'bih': 'ba',
  'bulgarie': 'bg', 'bulgaria': 'bg',
  'albanie': 'al', 'albania': 'al',
  'macedoine': 'mk', 'north macedonia': 'mk', 'macedonia': 'mk',
  'slovenie': 'si', 'slovenia': 'si',
  // Confédération AFC (Asie)
  'japon': 'jp', 'japan': 'jp', 'jpn': 'jp',
  'coree du sud': 'kr', 'south korea': 'kr', 'korea': 'kr', 'kor': 'kr', 'korea republic': 'kr', 'republic of korea': 'kr',
  'iran': 'ir', 'irn': 'ir',
  'australie': 'au', 'australia': 'au', 'aus': 'au',
  'arabie saoudite': 'sa', 'saudi arabia': 'sa', 'ksa': 'sa',
  'qatar': 'qa', 'qat': 'qa',
  'emirats': 'ae', 'uae': 'ae', 'uea': 'ae',
  'irak': 'iq', 'iraq': 'iq',
  'chine': 'cn', 'china': 'cn',
  // OFC
  'nouvelle zelande': 'nz', 'new zealand': 'nz', 'nzl': 'nz',
  // Autres
  'haiti': 'ht', 'haïti': 'ht',
  'jamaique': 'jm', 'jamaica': 'jm', 'jam': 'jm',
  'panama': 'pa', 'pan': 'pa',
  'costa rica': 'cr', 'crc': 'cr',
  'honduras': 'hn', 'hon': 'hn',
  'curacao': 'cw', 'curaçao': 'cw',
  'jordanie': 'jo', 'jordan': 'jo',
  'ouzbekistan': 'uz', 'uzbekistan': 'uz', 'uzb': 'uz',
};

// Mapping anglais (et variantes) → français pour l'affichage du nom de match.
// La page publique affiche TOUJOURS le nom de pays en français pour rester cohérent.
const TEAM_NAME_FR = {
  // CONCACAF
  'mexico': 'Mexique', 'mex': 'Mexique',
  'usa': 'États-Unis', 'united states': 'États-Unis', 'us': 'États-Unis',
  'canada': 'Canada', 'can': 'Canada',
  'haiti': 'Haïti', 'haïti': 'Haïti',
  'jamaica': 'Jamaïque', 'jam': 'Jamaïque',
  'panama': 'Panama', 'pan': 'Panama',
  'costa rica': 'Costa Rica', 'crc': 'Costa Rica',
  'honduras': 'Honduras', 'hon': 'Honduras',
  'curacao': 'Curaçao', 'curaçao': 'Curaçao',
  // CAF
  's.africa': 'Afrique du Sud', 'south africa': 'Afrique du Sud', 'rsa': 'Afrique du Sud',
  'morocco': 'Maroc', 'mar': 'Maroc',
  'algeria': 'Algérie', 'alg': 'Algérie',
  'tunisia': 'Tunisie',
  'egypt': 'Égypte',
  'senegal': 'Sénégal', 'sen': 'Sénégal',
  'cameroon': 'Cameroun',
  'nigeria': 'Nigeria', 'nig': 'Nigeria',
  'ghana': 'Ghana',
  'ivory coast': "Côte d'Ivoire", "cote d'ivoire": "Côte d'Ivoire", 'cote ivoire': "Côte d'Ivoire",
  'dr congo': 'RD Congo', 'rd congo': 'RD Congo', 'drc': 'RD Congo', 'congo': 'RD Congo',
  'cape verde': 'Cabo Verde', 'cabo verde': 'Cabo Verde', 'cab': 'Cabo Verde', 'cpv': 'Cabo Verde',
  'mali': 'Mali',
  'burkina faso': 'Burkina Faso', 'burkina': 'Burkina Faso',
  // CONMEBOL
  'brazil': 'Brésil', 'brasil': 'Brésil', 'bra': 'Brésil',
  'argentina': 'Argentine', 'arg': 'Argentine',
  'uruguay': 'Uruguay', 'uru': 'Uruguay',
  'colombia': 'Colombie', 'col': 'Colombie',
  'chile': 'Chili',
  'peru': 'Pérou',
  'ecuador': 'Équateur',
  'paraguay': 'Paraguay', 'par': 'Paraguay',
  'venezuela': 'Venezuela', 'ven': 'Venezuela',
  'bolivia': 'Bolivie',
  // UEFA
  'france': 'France', 'fra': 'France',
  'spain': 'Espagne', 'esp': 'Espagne',
  'germany': 'Allemagne', 'ger': 'Allemagne', 'deutschland': 'Allemagne',
  'italy': 'Italie', 'ita': 'Italie',
  'england': 'Angleterre', 'eng': 'Angleterre',
  'portugal': 'Portugal', 'por': 'Portugal',
  'netherlands': 'Pays-Bas', 'holland': 'Pays-Bas', 'ned': 'Pays-Bas',
  'belgium': 'Belgique', 'bel': 'Belgique',
  'croatia': 'Croatie', 'cro': 'Croatie',
  'switzerland': 'Suisse', 'sui': 'Suisse',
  'poland': 'Pologne', 'pol': 'Pologne',
  'turkey': 'Turquie', 'tur': 'Turquie',
  'denmark': 'Danemark', 'den': 'Danemark',
  'sweden': 'Suède', 'swe': 'Suède',
  'norway': 'Norvège', 'nor': 'Norvège',
  'serbia': 'Serbie', 'srb': 'Serbie',
  'austria': 'Autriche', 'aut': 'Autriche',
  'ukraine': 'Ukraine', 'ukr': 'Ukraine',
  'scotland': 'Écosse', 'sco': 'Écosse',
  'wales': 'Pays de Galles', 'wal': 'Pays de Galles',
  'ireland': 'Irlande', 'irl': 'Irlande',
  'czech republic': 'République Tchèque', 'czechia': 'République Tchèque', 'cze': 'République Tchèque',
  'slovakia': 'Slovaquie', 'svk': 'Slovaquie',
  'hungary': 'Hongrie', 'hun': 'Hongrie',
  'romania': 'Roumanie', 'rou': 'Roumanie',
  'greece': 'Grèce', 'gre': 'Grèce',
  'iceland': 'Islande', 'isl': 'Islande',
  'finland': 'Finlande', 'fin': 'Finlande',
  'bosnia and herzegovina': 'Bosnie-Herzégovine', 'bosnia': 'Bosnie-Herzégovine', 'bos': 'Bosnie-Herzégovine', 'bih': 'Bosnie-Herzégovine',
  'north macedonia': 'Macédoine du Nord', 'macedonia': 'Macédoine du Nord',
  'slovenia': 'Slovénie',
  'albania': 'Albanie',
  'bulgaria': 'Bulgarie',
  // AFC
  'japan': 'Japon', 'jpn': 'Japon',
  'south korea': 'Corée du Sud', 'korea': 'Corée du Sud', 'kor': 'Corée du Sud', 'korea republic': 'Corée du Sud', 'republic of korea': 'Corée du Sud',
  'iran': 'Iran', 'irn': 'Iran',
  'australia': 'Australie', 'aus': 'Australie',
  'saudi arabia': 'Arabie Saoudite', 'ksa': 'Arabie Saoudite',
  'qatar': 'Qatar', 'qat': 'Qatar',
  'uae': 'Émirats Arabes Unis', 'uea': 'Émirats Arabes Unis',
  'iraq': 'Irak',
  'china': 'Chine',
  'jordan': 'Jordanie',
  'uzbekistan': 'Ouzbékistan', 'uzb': 'Ouzbékistan',
  // OFC
  'new zealand': 'Nouvelle-Zélande', 'nzl': 'Nouvelle-Zélande',
};

const translateTeamName = (raw) => {
  if (!raw) return raw;
  const key = String(raw).toLowerCase().trim();
  return TEAM_NAME_FR[key] || raw;
};

// Traduit les noms d'équipes dans le titre du match (anglais → français).
// "M2 KOREA REPUBLIC VS CZECH REPUBLIC - WORLD CUP - GROUP A"
//   → "M2 CORÉE DU SUD VS RÉPUBLIQUE TCHÈQUE - WORLD CUP - GROUP A"
const translateMatchTitle = (eventName) => {
  if (!eventName) return eventName;
  const teams = parseMatchTeams(eventName);
  if (teams.length !== 2) return eventName;
  const fr1 = translateTeamName(teams[0]);
  const fr2 = translateTeamName(teams[1]);
  // Si aucune traduction n'a eu lieu, on retourne le nom original
  if (fr1.toLowerCase() === teams[0] && fr2.toLowerCase() === teams[1]) return eventName;
  // Remplace les noms originaux par les versions FR (insensible à la casse)
  // On recherche " {team1} VS {team2} " ou " {team1} vs {team2} "
  // et on remplace dans le titre original tout en gardant le reste.
  let out = eventName;
  // Regex builder qui échappe les caractères spéciaux des noms
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Construit un pattern qui matche "team1 vs team2" exactement
  const pattern = new RegExp(`(${escape(teams[0])})\\s+vs\\s+(${escape(teams[1])})`, 'i');
  out = out.replace(pattern, `${fr1.toUpperCase()} VS ${fr2.toUpperCase()}`);
  return out;
};

// Parse le nom d'un match pour en extraire les 2 équipes (ex: "M1 MEXICO VS S.AFRICA" → ['mexico','s.africa'])
const parseMatchTeams = (eventName) => {
  if (!eventName) return [];
  // Strip prefix like "M1 ", "M59 ", etc., and suffix " - World Cup..." / " . WC..."
  let clean = eventName
    .replace(/^[Mm]\d+\s+/, '')
    .replace(/[\s.,-]+(world\s*cup|wc).*$/i, '')
    .replace(/[\s.,-]+\(.+\)$/i, '')
    .trim();
  // Try to split on VS / vs / V (with surrounding spaces)
  const m = clean.split(/\s+(?:vs|VS|v)\s+/i);
  if (m.length !== 2) return [];
  return m.map(t => t.toLowerCase().trim());
};

const FlagIcon = ({ team, size = 'md' }) => {
  const dims = size === 'lg' ? 'h-10 w-14' : size === 'sm' ? 'h-4 w-6' : 'h-6 w-9';
  const iso = COUNTRY_TO_ISO[team.toLowerCase().trim()];
  if (!iso) {
    return (
      <div className={`${dims} rounded-sm bg-zinc-800 border border-zinc-700 flex items-center justify-center text-[10px] text-zinc-500 uppercase font-bold`} title={team}>
        {team.slice(0, 3)}
      </div>
    );
  }
  return (
    <img
      src={`https://flagcdn.com/w80/${iso}.png`}
      srcSet={`https://flagcdn.com/w160/${iso}.png 2x`}
      alt={team}
      title={team}
      className={`${dims} object-cover rounded-sm shadow-md ring-1 ring-black/30`}
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  );
};

const MatchFlags = ({ eventName, size = 'md' }) => {
  const teams = parseMatchTeams(eventName);
  if (teams.length !== 2) return null;
  return (
    <div className="flex items-center gap-2">
      <FlagIcon team={teams[0]} size={size} />
      <span className="text-zinc-500 text-xs font-bold">VS</span>
      <FlagIcon team={teams[1]} size={size} />
    </div>
  );
};

const projectAccentColor = (slug) => {
  const map = {
    'world-cup-2026': { from: 'from-red-500/15', via: 'via-zinc-900', glow: 'shadow-red-500/20' },
    'roland-garros': { from: 'from-orange-500/15', via: 'via-zinc-900', glow: 'shadow-orange-500/20' },
    'psg': { from: 'from-blue-500/15', via: 'via-zinc-900', glow: 'shadow-blue-500/20' },
  };
  return map[slug.toLowerCase()] || { from: 'from-amber-500/10', via: 'via-zinc-900', glow: 'shadow-amber-500/20' };
};

const formatPlace = (t) => {
  if (t.place && t.place_fin && t.place !== t.place_fin) {
    return `${t.place}–${t.place_fin}`;
  }
  return t.place || '—';
};

// Parse une place (ex: "12", "12-15", "A-12") pour récupérer un numéro de tri.
// Retourne null si pas un nombre simple (place type "VIP", lettres seules, etc.)
const _placeToNum = (place) => {
  if (place == null) return null;
  const s = String(place).trim();
  const m = s.match(/^-?\d+$/);
  if (m) return parseInt(s, 10);
  return null;
};

// Regroupe les tickets contigus d'une catégorie en "lots".
// Un lot = même block + même rang + places consécutives + même prix.
// Les tickets isolés (non contigus ou avec place non-numérique) restent en lots de 1.
const groupContiguousTickets = (tickets) => {
  if (!tickets || tickets.length === 0) return [];
  // Trier par block, rang, place_num pour détection des consécutifs
  const sorted = [...tickets].sort((a, b) => {
    const blockCmp = String(a.block || '').localeCompare(String(b.block || ''), undefined, { numeric: true });
    if (blockCmp) return blockCmp;
    const rangCmp = String(a.rang || '').localeCompare(String(b.rang || ''), undefined, { numeric: true });
    if (rangCmp) return rangCmp;
    const an = _placeToNum(a.place);
    const bn = _placeToNum(b.place);
    if (an !== null && bn !== null) return an - bn;
    return String(a.place || '').localeCompare(String(b.place || ''));
  });

  const groups = [];
  let current = null;
  for (const t of sorted) {
    const placeNum = _placeToNum(t.place);
    // On peut empiler dans le groupe en cours si même block/rang/prix et place_num = précédent+1
    const canStack = current
      && placeNum !== null
      && current.lastPlaceNum !== null
      && placeNum === current.lastPlaceNum + 1
      && (t.block || '') === current.block
      && (t.rang || '') === current.rang
      && Number(t.prix || 0) === Number(current.prix || 0);
    // Cas spécial : tickets "sans emplacement" (ni block, ni rang, ni place)
    // → tous regroupés ensemble même s'ils ne sont pas "consécutifs" (puisqu'il
    //   n'y a pas de notion d'ordre). Exemple : 32 tickets "CAT 2 sans emplacement"
    //   → 1 seule ligne "CAT 2 sans emplacement · Pack 32".
    const noLoc = !t.block && !t.rang && !t.place;
    const currentNoLoc = current && !current.block && !current.rang && !current.startPlace;
    const canStackNoLoc = current
      && noLoc
      && currentNoLoc
      && Number(t.prix || 0) === Number(current.prix || 0);
    if (canStack || canStackNoLoc) {
      current.tickets.push(t);
      if (placeNum !== null) {
        current.lastPlaceNum = placeNum;
        current.endPlace = t.place;
      }
    } else {
      if (current) groups.push(current);
      current = {
        id: t.id,
        block: t.block || '',
        rang: t.rang || '',
        prix: t.prix,
        startPlace: t.place,
        endPlace: t.place,
        lastPlaceNum: placeNum,
        tickets: [t],
      };
    }
  }
  if (current) groups.push(current);
  return groups;
};

const formatPrice = (prix, lang = 'fr') => {
  // ⚠️ POLICY : tant qu'on n'a pas activé une option admin "Afficher les prix",
  // on retourne toujours "Sur demande" / "On request" — même si la base
  // contient un `prix_diffusion` ou `prix`. Évite l'affichage involontaire.
  return lang === 'en' ? 'On request' : 'Sur demande';
};

const formatDate = (iso, lang = 'fr') => {
  if (!iso) return '';
  // Support pour format français de l'admin: "jeu. 11/06/2026 13:00" ou "11/06/2026"
  const frMatch = String(iso).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (frMatch) {
    const [, dd, mm, yyyy] = frMatch;
    const monthsFr = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
    const monthsEn = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const months = lang === 'en' ? monthsEn : monthsFr;
    const monthIdx = Math.max(0, Math.min(11, parseInt(mm, 10) - 1));
    let weekday = '';
    try {
      const d = new Date(parseInt(yyyy, 10), monthIdx, parseInt(dd, 10));
      if (!isNaN(d.getTime())) {
        const wd = d.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', { weekday: 'short' });
        weekday = wd.replace('.', '');
      }
    } catch { /* ignore */ }
    if (lang === 'en') {
      return `${weekday ? weekday + ', ' : ''}${months[monthIdx]} ${parseInt(dd, 10)}, ${yyyy}`;
    }
    return `${weekday ? weekday + '. ' : ''}${dd} ${months[monthIdx]} ${yyyy}`;
  }
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(lang === 'en' ? 'en-US' : 'fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};

// Compte à rebours "J-X" jusqu'à la date de l'événement
const computeCountdown = (iso, lang = 'fr') => {
  if (!iso) return null;
  const frMatch = String(iso).match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  let target = null;
  if (frMatch) {
    const [, dd, mm, yyyy] = frMatch;
    target = new Date(parseInt(yyyy, 10), parseInt(mm, 10) - 1, parseInt(dd, 10));
  } else {
    target = new Date(iso);
  }
  if (!target || isNaN(target.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.round((target - now) / 86400000);
  const labels = lang === 'en'
    ? { past: 'Past', today: 'Today', tomorrow: 'Tomorrow', d: 'D' }
    : { past: 'Passé', today: "Aujourd'hui", tomorrow: 'Demain', d: 'J' };
  if (diffDays < 0) return { label: labels.past, tone: 'past' };
  if (diffDays === 0) return { label: labels.today, tone: 'today' };
  if (diffDays === 1) return { label: labels.tomorrow, tone: 'soon' };
  if (diffDays <= 7) return { label: `${labels.d}-${diffDays}`, tone: 'soon' };
  if (diffDays <= 30) return { label: `${labels.d}-${diffDays}`, tone: 'normal' };
  return { label: `${labels.d}-${diffDays}`, tone: 'far' };
};

// Hook : détecte si l'écran est en mode mobile (< 768px).
// S'actualise en temps réel lors du redimensionnement de la fenêtre.
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return isMobile;
};

// Normalise le nom d'un match au format unifié : "M{N} TEAM1 VS TEAM2. WC - GROUP X"
// Exemples d'entrée :
//   "M1 MEXICO VS S.AFRICA . WC - GROUP A" → inchangé
//   "Turkey vs United States - World Cup - Group D (Match 59)" → "M59 TURKEY VS UNITED STATES. WC - GROUP D"
const normalizeMatchName = (raw) => {
  if (!raw) return raw;
  let s = String(raw).trim();
  // Already in M{N} format ?
  if (/^M\d+\s/.test(s)) return s.toUpperCase();
  // Extract match number from "(Match 59)" or "Match 59" at end
  let matchNum = null;
  const numMatch = s.match(/\(?\s*match\s+(\d+)\s*\)?/i);
  if (numMatch) {
    matchNum = numMatch[1];
    s = s.replace(numMatch[0], '').trim();
  }
  // Extract group
  let group = null;
  const grpMatch = s.match(/group\s+([a-z0-9]+)/i);
  if (grpMatch) {
    group = grpMatch[1].toUpperCase();
    s = s.replace(grpMatch[0], '').trim();
  }
  // Replace "World Cup" → "WC"
  s = s.replace(/world\s*cup/gi, 'WC');
  // Strip leading/trailing separators
  s = s.replace(/^[\s\-.,]+|[\s\-.,]+$/g, '');
  // Try to detect "TEAM1 vs TEAM2" early and only keep that part
  const vsM = s.match(/^(.*?)\s+(?:vs|VS|v)\s+(.+?)(?:\s*[-—,.]+\s*WC.*)?$/i);
  let teams = '';
  if (vsM) {
    teams = `${vsM[1].trim().toUpperCase()} VS ${vsM[2].trim().toUpperCase()}`;
  } else {
    teams = s.toUpperCase();
  }
  const prefix = matchNum ? `M${matchNum} ` : '';
  const suffixParts = ['WC'];
  if (group) suffixParts.push(`GROUP ${group}`);
  return `${prefix}${teams}. ${suffixParts.join(' - ')}`;
};

// Style du badge catégorie sur la version mobile — couleurs officielles FIFA.
// CAT 1 = orange, CAT 2 = rouge, CAT 3 = bleu, CAT 4 = vert.
// SUP (catégories supporters / fan packs) en violet / rose / fuchsia.
// Reconnaît plusieurs variantes : "CAT 2", "CATEGORIE 2", "CATÉGORIE 2", "CATEGORY 2", "SUP 1", "SUPPORTER 1", etc.
const catBadgeStyle = (cat) => {
  const u = String(cat || '').toUpperCase();
  const catNum = u.match(/^\s*CAT[A-ZÉ]*\s*(\d+)/);
  if (catNum) {
    const n = parseInt(catNum[1], 10);
    if (n === 1) return 'bg-gradient-to-br from-amber-500/30 to-orange-600/25 text-amber-200 border border-amber-400/50 shadow-sm shadow-amber-500/20';
    if (n === 2) return 'bg-gradient-to-br from-rose-500/30 to-red-700/25 text-rose-200 border border-rose-400/50 shadow-sm shadow-rose-500/20';
    if (n === 3) return 'bg-gradient-to-br from-sky-500/30 to-blue-700/25 text-sky-200 border border-sky-400/50 shadow-sm shadow-sky-500/20';
    if (n === 4) return 'bg-gradient-to-br from-emerald-500/30 to-green-700/25 text-emerald-200 border border-emerald-400/50 shadow-sm shadow-emerald-500/20';
  }
  const supNum = u.match(/^\s*SUP[A-Z]*\s*(\d+)/);
  if (supNum) {
    const n = parseInt(supNum[1], 10);
    if (n === 1) return 'bg-gradient-to-br from-purple-500/30 to-violet-700/25 text-purple-200 border border-purple-400/50 shadow-sm shadow-purple-500/20';
    if (n === 2) return 'bg-gradient-to-br from-pink-500/30 to-fuchsia-700/25 text-pink-200 border border-pink-400/50 shadow-sm shadow-pink-500/20';
    return 'bg-gradient-to-br from-fuchsia-500/30 to-purple-700/25 text-fuchsia-200 border border-fuchsia-400/50 shadow-sm shadow-fuchsia-500/20';
  }
  return 'bg-zinc-700/40 text-zinc-300 border border-zinc-600/40';
};

// Libellé court de la catégorie pour l'affichage mobile (CAT 1, SUP 2, etc.)
const catDisplayLabel = (cat) => {
  const u = String(cat || '').toUpperCase();
  const cm = u.match(/^\s*CAT[A-ZÉ]*\s*(\d+)/);
  if (cm) return `CAT ${cm[1]}`;
  const sm = u.match(/^\s*SUP[A-Z]*\s*(\d+)/);
  if (sm) return `SUP ${sm[1]}`;
  return cat;
};

// Tri des catégories sur mobile : CAT 1 → CAT 4 d'abord, puis SUP 1 → SUP N, puis le reste alpha.
const catSortKey = (cat) => {
  const u = String(cat || '').toUpperCase();
  const cm = u.match(/^\s*CAT[A-ZÉ]*\s*(\d+)/);
  if (cm) return `1_${String(cm[1]).padStart(3, '0')}_${u}`;
  const sm = u.match(/^\s*SUP[A-Z]*\s*(\d+)/);
  if (sm) return `2_${String(sm[1]).padStart(3, '0')}_${u}`;
  if (u.startsWith('SUP')) return `2_999_${u}`;
  return `3_${u}`;
};

const CountdownBadge = ({ iso, lang = 'fr' }) => {
  const c = computeCountdown(iso, lang);
  if (!c) return null;
  const styles = {
    today: 'bg-red-500 text-white animate-pulse',
    soon: 'bg-amber-500 text-zinc-950',
    normal: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
    far: 'bg-zinc-800 text-zinc-400 border border-zinc-700',
    past: 'bg-zinc-700 text-zinc-500 line-through',
  }[c.tone] || 'bg-zinc-800 text-zinc-300';
  return (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider ${styles}`}>
      {c.label}
    </span>
  );
};

const PublicSalePage = () => {
  const { projectSlug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [expandedCats, setExpandedCats] = useState(new Set());
  // Filtres multi-sélection
  const [selectedMatches, setSelectedMatches] = useState(new Set()); // event names
  const [selectedCats, setSelectedCats] = useState(new Set()); // category names
  const [matchDropdownOpen, setMatchDropdownOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  // Modal plan du stade
  const [stadiumModal, setStadiumModal] = useState(null); // { event, stadium }
  // Langue : 'en' (défaut) ou 'fr'. Persisté en localStorage.
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem('public_lang');
      return saved === 'fr' ? 'fr' : 'en';
    } catch { return 'en'; }
  });
  const L = LABELS[lang];
  const setLanguage = (l) => {
    setLang(l);
    try { localStorage.setItem('public_lang', l); } catch { /* ignore */ }
  };
  // Détection mobile : on adapte le layout (cartes au lieu de tableau, etc.)
  const isMobile = useIsMobile();

  // Charger les plans custom une fois (override les URLs par défaut de stadiums.js)
  useEffect(() => {
    axios.get(`${API_BASE}/api/stadiums/custom-plans`).then(res => {
      applyCustomPlans(res.data || {});
    }).catch(() => { /* ignore — fallback aux URLs par défaut */ });
  }, []);

  const fetchData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      const res = await axios.get(`${API_BASE}/api/public/vente/${projectSlug}`, { params });
      setData(res.data);
      // 🎯 Tout déplier d'office au chargement initial (pas sur les refresh silencieux,
      // pour préserver les replis manuels de l'utilisateur).
      if (!silent && res.data?.events?.length > 0) {
        setExpandedEvents(new Set(res.data.events.map(e => e.evenement)));
        // Aussi déplier toutes les catégories
        const allCats = new Set();
        for (const ev of res.data.events) {
          for (const cat of Object.keys(ev.categories || {})) {
            allCats.add(`${ev.evenement}|||${cat}`);
          }
        }
        setExpandedCats(allCats);
      }
    } catch (e) {
      if (!silent) setError(e.response?.data?.detail || 'Erreur de chargement');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug]);

  // 🔄 WebSocket public-stock : refresh INSTANTANÉ quand l'admin modifie le stock
  // (toggle DIFFUSER, vente, attribution, création, suppression). Fallback polling
  // 30s si la connexion WS échoue (réseau strict, proxy qui bloque WS, etc.).
  useEffect(() => {
    if (!projectSlug) return;
    let ws = null;
    let pollInterval = null;
    let wsKeepAlive = null;
    let wsHealthy = false;
    let cancelled = false;

    const startPolling = () => {
      if (pollInterval) return;
      pollInterval = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
        fetchData({ silent: true });
      }, 30000);
    };

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const connectWS = () => {
      if (cancelled) return;
      try {
        const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        // Sur custom domain on a API_BASE='' → relatif (= même host). Sinon BACKEND_URL.
        const wsBase = API_BASE
          ? API_BASE.replace(/^http/, 'ws')
          : `${proto}//${window.location.host}`;
        ws = new WebSocket(`${wsBase}/api/ws/public-stock/${projectSlug}`);
        ws.onopen = () => {
          wsHealthy = true;
          stopPolling(); // WS marche → pas besoin du polling
          // Keep-alive : ping toutes les 25 s pour éviter que les proxys coupent la connexion
          wsKeepAlive = setInterval(() => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              try { ws.send('ping'); } catch { /* ignore */ }
            }
          }, 25000);
        };
        ws.onmessage = (evt) => {
          try {
            const msg = JSON.parse(evt.data);
            if (msg && msg.type === 'stock_changed') {
              fetchData({ silent: true });
            }
          } catch { /* ignore */ }
        };
        ws.onclose = () => {
          if (wsKeepAlive) { clearInterval(wsKeepAlive); wsKeepAlive = null; }
          // Si la connexion n'a jamais été healthy → tombe en polling tout de suite
          if (!wsHealthy) startPolling();
          // Sinon, tentative de reconnexion dans 3 s
          if (!cancelled) {
            setTimeout(() => { if (!cancelled) connectWS(); }, 3000);
          }
        };
        ws.onerror = () => {
          // Pas besoin de log bruyant : le onclose va gérer le fallback
        };
      } catch {
        startPolling();
      }
    };

    connectWS();

    return () => {
      cancelled = true;
      if (wsKeepAlive) clearInterval(wsKeepAlive);
      if (pollInterval) clearInterval(pollInterval);
      if (ws) {
        try { ws.close(); } catch { /* ignore */ }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug]);

  // Refresh immédiat quand l'onglet redevient actif (l'utilisateur revient à la page)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSlug, query]);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const toggleEvent = (key) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const toggleCat = (key) => {
    setExpandedCats(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    if (!data) return;
    const allEvents = new Set(data.events.map(e => e.evenement));
    const allCats = new Set();
    data.events.forEach(e => Object.keys(e.categories || {}).forEach(c => allCats.add(`${e.evenement}|||${c}`)));
    setExpandedEvents(allEvents);
    setExpandedCats(allCats);
  };

  const collapseAll = () => {
    setExpandedEvents(new Set());
    setExpandedCats(new Set());
  };

  // Résoudre le stade d'un événement
  const getStadiumFor = (event) => {
    return findStadium(event.lieu, '', event.evenement, 'WORLD CUP 2026');
  };

  // Liste de tous les matchs disponibles (pour le filtre)
  const allMatches = (data?.events || []).map(e => e.evenement);
  // Liste de toutes les catégories disponibles (déduplication)
  const allCats = Array.from(new Set(
    (data?.events || []).flatMap(e => Object.keys(e.categories || {}))
  )).sort();

  // Events filtrés selon filtres multi-sélection
  const filteredEvents = (data?.events || []).filter(e => {
    if (selectedMatches.size > 0 && !selectedMatches.has(e.evenement)) return false;
    if (selectedCats.size > 0) {
      const eventCats = Object.keys(e.categories || {});
      if (!eventCats.some(c => selectedCats.has(c))) return false;
    }
    return true;
  }).map(e => {
    // Si filtre catégorie actif, ne garder que les cats sélectionnées dans chaque event
    if (selectedCats.size === 0) return e;
    const filteredCats = {};
    Object.entries(e.categories || {}).forEach(([c, tickets]) => {
      if (selectedCats.has(c)) filteredCats[c] = tickets;
    });
    return { ...e, categories: filteredCats, total: Object.values(filteredCats).reduce((s, t) => s + t.length, 0) };
  });

  const toggleMatchFilter = (m) => {
    setSelectedMatches(prev => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m); else next.add(m);
      return next;
    });
  };
  const toggleCatFilter = (c) => {
    setSelectedCats(prev => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };

  const totalMatches = filteredEvents.length;

  return (
    <div className="min-h-screen text-white relative" style={{
      background: 'linear-gradient(rgba(9, 9, 11, 0.72), rgba(9, 9, 11, 0.82)), url(https://customer-assets.emergentagent.com/job_289d1ff2-678c-4880-b158-8ad7c1edcdb9/artifacts/3lgyi1im_hero-adidas-trionda-fifa-worldcup-ball6-1400x788%20%281%29.webp) center/cover fixed no-repeat'
    }}>
      <Toaster position="top-right" theme="dark" />
      {/* Custom scrollbar for public ticket lists */}
      <style>{`
        .public-tickets-scroll::-webkit-scrollbar { width: 10px; }
        .public-tickets-scroll::-webkit-scrollbar-track { background: rgba(24,24,27,0.6); border-radius: 8px; }
        .public-tickets-scroll::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #C4A052 0%, #8a6f30 100%); border-radius: 8px; border: 2px solid rgba(9,9,11,0.6); }
        .public-tickets-scroll::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, #d4b160 0%, #9a7f3e 100%); }
        .public-tickets-scroll { scrollbar-color: #C4A052 rgba(24,24,27,0.6); scrollbar-width: thin; }
      `}</style>
      
      {/* Hero header */}
      <div className="relative overflow-hidden border-b border-zinc-800">
        <div className={`absolute inset-0 bg-gradient-to-br ${projectAccentColor(projectSlug).from} ${projectAccentColor(projectSlug).via} to-zinc-950`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8">
          {projectHeroImage(projectSlug) ? (
            <div className="relative shrink-0 hidden sm:flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-amber-400/30 via-red-500/10 to-transparent rounded-2xl blur-2xl" />
              <img
                src={projectHeroImage(projectSlug)}
                alt={projectDisplayName(projectSlug)}
                className="relative h-28 w-28 sm:h-32 sm:w-32 object-contain rounded-2xl shadow-2xl ring-1 ring-amber-500/30 bg-black/40 p-2"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            </div>
          ) : (
            <ProjectFallbackVisual slug={projectSlug} />
          )}
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold tracking-tight">{projectDisplayName(projectSlug)}</h1>
            <p className="text-zinc-300 mt-1 sm:mt-2 text-xs sm:text-sm">
              {L.listPublic}
              {data && (
                <>
                  {' '}<span className="text-amber-400 font-semibold">{data.total}</span> {data.total !== 1 ? L.tickets : L.ticket} {L.inStock}
                  {' · '}<span className="text-amber-400 font-semibold">{totalMatches}</span> {totalMatches !== 1 ? L.matches : L.match}.
                </>
              )}
            </p>
          </div>
          {/* Sélecteur de langue EN / FR (EN par défaut) */}
          <div className="shrink-0 self-end sm:self-auto flex items-center gap-1 bg-zinc-900/80 border border-zinc-700 rounded-full p-1" data-testid="lang-switcher">
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider transition ${lang === 'en' ? 'bg-amber-500 text-zinc-950 shadow-lg shadow-amber-500/30' : 'text-zinc-400 hover:text-white'}`}
              data-testid="btn-lang-en"
              title="English"
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('fr')}
              className={`px-3 py-1 rounded-full text-xs font-bold tracking-wider transition ${lang === 'fr' ? 'bg-amber-500 text-zinc-950 shadow-lg shadow-amber-500/30' : 'text-zinc-400 hover:text-white'}`}
              data-testid="btn-lang-fr"
              title="Français"
            >
              FR
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="sticky top-0 z-20 bg-zinc-950/70 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3 flex flex-wrap items-center gap-2 sm:gap-3">
          {/* Filtre Matches */}
          <div className="relative">
            <button
              onClick={() => { setMatchDropdownOpen(v => !v); setCatDropdownOpen(false); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition flex items-center gap-1.5 ${selectedMatches.size > 0 ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800'}`}
              data-testid="match-filter-btn"
            >
              {L.matchesLbl} {selectedMatches.size > 0 && <span className="ml-1 px-1.5 py-0 rounded-full bg-amber-500 text-zinc-950 text-[10px]">{selectedMatches.size}</span>}
              <ChevronDown className="h-3 w-3" />
            </button>
            {matchDropdownOpen && (
              <div className="absolute z-30 mt-1 w-72 max-h-80 overflow-auto bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-2" data-testid="match-filter-dropdown">
                <div className="flex items-center justify-between mb-2 px-2">
                  <button onClick={() => setSelectedMatches(new Set(allMatches))} className="text-xs text-amber-400 hover:underline">Tout cocher</button>
                  <button onClick={() => setSelectedMatches(new Set())} className="text-xs text-zinc-500 hover:text-white">Effacer</button>
                </div>
                {allMatches.map(m => (
                  <label key={m} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-zinc-800 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedMatches.has(m)}
                      onChange={() => toggleMatchFilter(m)}
                      className="accent-amber-500"
                    />
                    <span className="text-zinc-200 truncate" title={normalizeMatchName(m)}>{normalizeMatchName(m)}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Filtre Catégories */}
          <div className="relative">
            <button
              onClick={() => { setCatDropdownOpen(v => !v); setMatchDropdownOpen(false); }}
              className={`px-3 py-1.5 text-xs font-semibold rounded-md border transition flex items-center gap-1.5 ${selectedCats.size > 0 ? 'bg-amber-500/15 text-amber-300 border-amber-500/40' : 'bg-zinc-900 text-zinc-300 border-zinc-800 hover:bg-zinc-800'}`}
              data-testid="cat-filter-btn"
            >
              {L.catsLbl} {selectedCats.size > 0 && <span className="ml-1 px-1.5 py-0 rounded-full bg-amber-500 text-zinc-950 text-[10px]">{selectedCats.size}</span>}
              <ChevronDown className="h-3 w-3" />
            </button>
            {catDropdownOpen && (
              <div className="absolute z-30 mt-1 w-64 max-h-80 overflow-auto bg-zinc-900 border border-zinc-800 rounded-lg shadow-2xl p-2" data-testid="cat-filter-dropdown">
                <div className="flex items-center justify-between mb-2 px-2">
                  <button onClick={() => setSelectedCats(new Set(allCats))} className="text-xs text-amber-400 hover:underline">Tout cocher</button>
                  <button onClick={() => setSelectedCats(new Set())} className="text-xs text-zinc-500 hover:text-white">Effacer</button>
                </div>
                {allCats.map(c => (
                  <label key={c} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-zinc-800 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCats.has(c)}
                      onChange={() => toggleCatFilter(c)}
                      className="accent-amber-500"
                    />
                    <span className="text-zinc-200">{c}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <input
              data-testid="public-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={L.search}
              className="w-full bg-zinc-900 border border-zinc-800 rounded-full pl-9 pr-4 py-2 text-sm placeholder:text-zinc-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Actions — masqué sur mobile (tout est déjà déplié d'office) */}
          <div className="hidden md:flex items-center gap-2 ml-auto">
            <button onClick={expandAll} className="px-3 py-1.5 text-xs font-semibold text-zinc-300 bg-zinc-900 border border-zinc-800 rounded-md hover:bg-zinc-800" data-testid="expand-all-btn">
              <ChevronDown className="h-3 w-3 inline mr-1" />{L.expandAll}
            </button>
            <button onClick={collapseAll} className="px-3 py-1.5 text-xs font-semibold text-zinc-300 bg-zinc-900 border border-zinc-800 rounded-md hover:bg-zinc-800" data-testid="collapse-all-btn">
              <ChevronRight className="h-3 w-3 inline mr-1" />{L.collapseAll}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-8">
        {loading && (
          <div className="text-center py-20 text-zinc-500" data-testid="public-loading">
            <div className="inline-block h-8 w-8 border-2 border-amber-500 border-r-transparent rounded-full animate-spin mb-3" />
            <p>{L.loading}</p>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-red-300" data-testid="public-error">
            <p className="font-semibold mb-1">{lang === 'en' ? 'Error' : 'Erreur'}</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {!loading && !error && filteredEvents.length === 0 && (
          <div className="text-center py-20" data-testid="public-empty">
            <Ticket className="h-12 w-12 text-zinc-700 mx-auto mb-3" />
            <p className="text-zinc-400 font-semibold">{L.noTickets}</p>
            <p className="text-zinc-600 text-sm mt-1">{L.comeBack}</p>
          </div>
        )}

        {!loading && !error && filteredEvents.map((event) => {
          const isOpen = expandedEvents.has(event.evenement);
          // 🏆 Source of truth : calendrier officiel WC 2026.
          // Si l'event a un match_num connu, on overrides l'affichage (titre, date, lieu, drapeaux)
          // pour ignorer toute donnée mal saisie côté admin.
          const official = getOfficialMatch(event.match_num);
          // Titre affiché : "M{N} HOME VS AWAY" — en FR ou EN selon préférence utilisateur
          const home = official ? (lang === 'en' ? official.home : translateTeamName(official.home)) : null;
          const away = official ? (lang === 'en' ? official.away : translateTeamName(official.away)) : null;
          const displayTitle = official
            ? `M${official.matchNum} ${String(home).toUpperCase()} VS ${String(away).toUpperCase()}`
            : translateMatchTitle(normalizeMatchName(event.evenement));
          // Date affichée : on prend celle du calendrier officiel si dispo
          const displayDate = official ? official.date : event.date_evenement;
          // Lieu affiché : stade + ville du calendrier officiel si dispo
          const displayVenue = official
            ? `${official.stadium} — ${official.city}`
            : event.lieu;
          // Drapeaux : depuis le calendrier officiel (équipes nettoyées) sinon depuis le nom event
          const flagTeams = official ? [official.home, official.away] : null;
          // Badge Groupe / Phase
          const phaseLabel = official ? (official.group
            ? `${L.group} ${official.group}`
            : getMatchPhase(official.matchNum, lang))
            : null;

          // === MOBILE branch ===
          // Aplatit toutes les catégories en une liste de lots (CAT 1→4 puis SUP 1→N),
          // affichée en cartes verticales avec badge de catégorie inline + "Pack N".
          if (isMobile) {
            const mobileLots = (() => {
              const sortedCats = Object.entries(event.categories || {}).sort(
                ([a], [b]) => catSortKey(a).localeCompare(catSortKey(b))
              );
              const out = [];
              for (const [cat, tickets] of sortedCats) {
                const groups = groupContiguousTickets(tickets);
                for (const g of groups) out.push({ ...g, category: cat });
              }
              return out;
            })();
            return (
              <div
                key={event.evenement}
                className="mb-2.5 bg-zinc-900/90 border border-zinc-700 rounded-2xl overflow-hidden ring-1 ring-white/5 shadow-xl shadow-black/40"
                data-testid={`event-card-${event.evenement}`}
              >
                <button
                  onClick={() => toggleEvent(event.evenement)}
                  className="w-full text-left p-3.5 hover:bg-zinc-800/40 transition"
                  data-testid={`event-toggle-${event.evenement}`}
                >
                  <div className="flex items-center gap-2">
                    {isOpen
                      ? <ChevronDown className="h-4 w-4 text-amber-400 shrink-0" />
                      : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0" />}
                    <div className="text-sm font-bold text-white leading-tight flex-1 min-w-0">{displayTitle}</div>
                    {flagTeams && (
                      <div className="flex items-center gap-1 shrink-0">
                        <FlagIcon team={flagTeams[0]} size="sm" />
                        <FlagIcon team={flagTeams[1]} size="sm" />
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-400 pl-6">
                    {phaseLabel && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30 font-bold uppercase tracking-wider text-[9px]">{phaseLabel}</span>
                    )}
                    {displayDate && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />{formatDate(displayDate, lang)}
                        {official && official.time && (
                          <span className="text-amber-400/80 font-semibold"> · {official.time}</span>
                        )}
                      </span>
                    )}
                  </div>
                  {displayVenue && (
                    <div className="mt-1 flex items-center gap-1 text-[11px] text-zinc-500 pl-6">
                      <MapPin className="h-3 w-3 shrink-0" />
                      <span className="truncate">{displayVenue}</span>
                    </div>
                  )}
                </button>
                {isOpen && mobileLots.length > 0 && (
                  <div className="border-t border-zinc-800/60 px-3.5 py-3 space-y-1.5">
                    {mobileLots.map((g) => {
                      const hasLoc = !!(g.block || g.rang);
                      const locText = hasLoc
                        ? `${L.block} ${g.block || '—'}${g.rang ? ` · ${L.row} ${g.rang}` : ''}`
                        : (lang === 'en' ? 'No location' : 'Sans emplacement');
                      const packLabel = g.tickets.length > 1
                        ? `Pack ${g.tickets.length}`
                        : (lang === 'en' ? '1 seat' : '1 place');
                      return (
                        <div
                          key={g.id}
                          className="bg-zinc-950/70 border border-zinc-800 rounded-lg p-3 flex items-center justify-between gap-2"
                          data-testid={`public-lot-${g.id}`}
                        >
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap ${catBadgeStyle(g.category)}`}>
                              {catDisplayLabel(g.category)}
                            </span>
                            <span className={`text-[13px] font-mono truncate ${hasLoc ? 'text-white font-bold' : 'text-zinc-400 italic'}`}>
                              {locText}
                            </span>
                          </div>
                          <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-800/40 text-emerald-200 border border-emerald-600/50 whitespace-nowrap shadow-sm shadow-emerald-900/40">
                            {packLabel}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          }

          // === DESKTOP branch (inchangé) ===
          return (
            <div key={event.evenement} className="mb-3 bg-zinc-900/85 backdrop-blur-sm border border-zinc-700 rounded-xl overflow-hidden shadow-xl shadow-black/40 ring-1 ring-white/5" data-testid={`event-card-${event.evenement}`}>
              {/* Event header — responsive : flex-col sur mobile, flex-row sur desktop */}
              <button
                onClick={() => toggleEvent(event.evenement)}
                className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 sm:px-5 py-3 sm:py-4 hover:bg-zinc-800/50 transition gap-3"
              >
                <div className="flex items-start sm:items-center gap-2 sm:gap-3 text-left min-w-0 flex-1">
                  {isOpen ? <ChevronDown className="h-4 w-4 text-amber-400 shrink-0 mt-1 sm:mt-0" /> : <ChevronRight className="h-4 w-4 text-zinc-500 shrink-0 mt-1 sm:mt-0" />}
                  {flagTeams ? (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <FlagIcon team={flagTeams[0]} size="lg" />
                      <span className="text-zinc-500 text-[10px] sm:text-xs font-bold">VS</span>
                      <FlagIcon team={flagTeams[1]} size="lg" />
                    </div>
                  ) : (
                    <MatchFlags eventName={event.evenement} size="lg" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white text-sm sm:text-base leading-tight">{displayTitle}</span>
                      {phaseLabel && (
                        <span className="px-2 py-0.5 text-[10px] font-bold tracking-wider uppercase rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                          {phaseLabel}
                        </span>
                      )}
                    </div>
                    {displayDate && (
                      <div className="text-[11px] sm:text-xs text-zinc-500 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1">
                        <Calendar className="h-3 w-3 shrink-0" />{formatDate(displayDate, lang)}
                        {official && official.time && (
                          <span className="text-amber-400/80 font-medium">· {official.time}</span>
                        )}
                        <CountdownBadge iso={displayDate} lang={lang} />
                        {displayVenue && (<><span className="hidden sm:inline">·</span><MapPin className="h-3 w-3 shrink-0" /><span className="truncate">{displayVenue}</span></>)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0 self-end sm:self-auto">
                  {(() => {
                    // Stade : on prend celui du calendrier officiel si dispo (plus fiable que `event.lieu` saisi en admin)
                    const stadium = official
                      ? findStadium(official.stadium, '', '', 'WORLD CUP 2026')
                      : getStadiumFor(event);
                    if (!stadium || !stadium.mapUrl) return null;
                    return (
                      <button
                        onClick={(e) => { e.stopPropagation(); setStadiumModal({ event, stadium }); }}
                        className="px-2.5 py-1 text-xs font-semibold rounded-md bg-zinc-800/80 hover:bg-amber-500 hover:text-zinc-950 text-amber-300 border border-amber-500/30 transition flex items-center gap-1"
                        title={`${L.stadiumPlan} : ${stadium.name}`}
                        data-testid={`btn-plan-${event.evenement}`}
                      >
                        <MapPin className="h-3 w-3" />
                        {L.plan}
                      </button>
                    );
                  })()}
                  <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-amber-400/15 text-amber-300 border border-amber-500/30">
                    {event.total} {event.total > 1 ? L.tickets : L.ticket}
                  </span>
                </div>
              </button>

              {/* Categories — triées : CAT 1→4 d'abord, puis SUP 1→N, puis alpha */}
              {isOpen && Object.entries(event.categories || {})
                .sort(([a], [b]) => catSortKey(a).localeCompare(catSortKey(b)))
                .map(([cat, tickets]) => {
                const catKey = `${event.evenement}|||${cat}`;
                const catOpen = expandedCats.has(catKey) || expandedCats.size === 0; // open by default
                return (
                  <div key={cat} className="border-t border-zinc-700" data-testid={`cat-block-${cat}`}>
                    <button
                      onClick={() => toggleCat(catKey)}
                      className="w-full flex items-center justify-between px-5 py-2.5 bg-zinc-950/60 hover:bg-zinc-800/60 transition border-l-4 border-amber-500"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        {catOpen ? <ChevronDown className="h-3 w-3 text-amber-400" /> : <ChevronRight className="h-3 w-3 text-zinc-500" />}
                        <span className="font-semibold text-amber-300">{cat}</span>
                        <span className="text-xs text-zinc-500">{tickets.length}</span>
                      </div>
                    </button>

                    {catOpen && (() => {
                      // Regroupe les tickets contigus en lots
                      const groups = groupContiguousTickets(tickets);
                      return (
                      <div className="bg-zinc-950/40">
                        {/* Column headers - sticky.
                            Sur mobile, on n'affiche PAS la colonne "Prix" (toujours "Sur demande"). */}
                        <div className="grid grid-cols-10 sm:grid-cols-12 text-[10px] uppercase tracking-wider text-zinc-400 px-3 sm:px-5 py-2 bg-zinc-950/95 backdrop-blur border-b border-zinc-700 sticky top-0 z-10">
                          <div className="col-span-3 sm:col-span-3">{L.block}</div>
                          <div className="col-span-2 sm:col-span-2">{L.row}</div>
                          <div className="col-span-3 sm:col-span-3">{L.seat}</div>
                          <div className="col-span-2 sm:col-span-2 text-center">Lot</div>
                          <div className="hidden sm:block sm:col-span-2 text-right">{L.price}</div>
                        </div>
                        {/* Scrollable lot list */}
                        <div
                          className={`divide-y divide-zinc-700/60 ${groups.length > 10 ? 'max-h-[440px] overflow-y-auto public-tickets-scroll' : ''}`}
                        >
                          {groups.map((g) => {
                            const placeRange = g.tickets.length > 1
                              ? `${g.startPlace}–${g.endPlace}`
                              : (g.startPlace || '—');
                            const lotLabel = g.tickets.length > 1
                              ? (lang === 'en' ? `Lot of ${g.tickets.length}` : `Lot de ${g.tickets.length}`)
                              : (lang === 'en' ? '1 seat' : '1 place');
                            return (
                              <div key={g.id} className="grid grid-cols-10 sm:grid-cols-12 px-3 sm:px-5 py-2.5 text-sm hover:bg-zinc-800/60 transition items-center" data-testid={`public-lot-${g.id}`}>
                                <div className="col-span-3 sm:col-span-3 font-mono text-amber-300/90 truncate">{g.block || '—'}</div>
                                <div className="col-span-2 sm:col-span-2 text-zinc-300 truncate">{g.rang || '—'}</div>
                                <div className="col-span-3 sm:col-span-3 text-zinc-300 truncate">{placeRange}</div>
                                <div className="col-span-2 sm:col-span-2 text-center">
                                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] sm:text-[11px] font-semibold whitespace-nowrap ${g.tickets.length > 1 ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'text-zinc-500'}`}>
                                    {lotLabel}
                                  </span>
                                </div>
                                <div className="hidden sm:block sm:col-span-2 text-right font-semibold text-emerald-400 text-xs sm:text-sm whitespace-nowrap">{formatPrice(g.prix, lang)}</div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      );
                    })()}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Modal Plan du stade */}
      {stadiumModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm" onClick={() => setStadiumModal(null)} data-testid="stadium-modal">
          <div className="relative bg-zinc-900 border border-amber-500/30 rounded-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-amber-400" />
                <div>
                  <div className="font-semibold text-white">{stadiumModal.stadium.name}</div>
                  <div className="text-xs text-zinc-500">
                    {stadiumModal.stadium.city || stadiumModal.event.lieu}
                    {stadiumModal.event.date_evenement && (
                      <> · {formatDate(stadiumModal.event.date_evenement, lang)}</>
                    )}
                  </div>
                </div>
              </div>
              <button onClick={() => setStadiumModal(null)} className="text-zinc-400 hover:text-white p-2 rounded hover:bg-zinc-800 transition" data-testid="close-stadium-modal">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="overflow-auto max-h-[80vh] bg-zinc-950 flex items-center justify-center p-4">
              <img
                src={stadiumModal.stadium.mapUrl}
                alt={`Plan ${stadiumModal.stadium.name}`}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
                onError={(e) => { e.currentTarget.outerHTML = '<div class="text-zinc-500 py-12">Plan indisponible</div>'; }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="border-t border-zinc-800 mt-12 py-6 text-center text-xs text-zinc-600">
        {L.poweredBy}
      </div>
    </div>
  );
};

export default PublicSalePage;
